import React, { useState, useEffect, useCallback } from 'react';
import './food.css';

// Admin index. PIN-gated with the same client-side food-log PIN as the
// upload pages — cosmetic (the PIN is in the bundle), so this page only
// links to things that gate themselves, and the one action it triggers
// (geocode backfill) re-checks the PIN server-side.
const CORRECT_PIN = process.env.REACT_APP_FOOD_LOG_PIN;

const VERCEL_PROJECT = 'sandyjswls-projects/sandeep-london';
const SUPABASE_PROJECT = 'xqnycpnljrqgrtleonga';
const PURGE_CMD = 'vercel cache purge --type cdn';

const UPLOADS = [
  { href: '/lunch/log', label: 'Single lunch photo', desc: 'One photo → one day' },
  { href: '/lunch/log/multi', label: 'Bulk lunch photos', desc: 'Many photos, dates read from EXIF' },
  { href: '/photos/upload', label: 'Map photos', desc: 'Geotagged shots for the world map' },
];

const MENU_PRIMARY = 'https://menu.sandeep.london';
const MENU_FALLBACK = 'https://fooditude-mocha.vercel.app';
const MENU_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const MENU_MEALS = ['breakfast', 'lunch'];

async function menuFetch(path) {
  try {
    return await fetch(`${MENU_PRIMARY}${path}`);
  } catch {
    return fetch(`${MENU_FALLBACK}${path}`);
  }
}

function MenuWidget() {
  const [menuState, setMenuState] = useState('loading'); // loading | ready | empty | error
  const [menu, setMenu] = useState(null);
  const [menuError, setMenuError] = useState('');
  const [reseed, setReseed] = useState({ state: 'idle', data: null, error: '' });
  const [openItem, setOpenItem] = useState(null);

  const loadMenu = useCallback(async () => {
    setMenuState('loading');
    try {
      const r = await menuFetch('/api/menu');
      if (r.status === 404) {
        setMenuState('empty');
        return;
      }
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      setMenu(data);
      setMenuState('ready');
    } catch (err) {
      setMenuError(err.message);
      setMenuState('error');
    }
  }, []);

  useEffect(() => { loadMenu(); }, [loadMenu]);

  async function runReseed() {
    setReseed({ state: 'running', data: null, error: '' });
    try {
      const r = await menuFetch('/api/cron/scrape');
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || data.error || `HTTP ${r.status}`);
      setReseed({ state: 'done', data, error: '' });
      await loadMenu();
    } catch (err) {
      setReseed({ state: 'error', data: null, error: err.message });
    }
  }

  const menuWarnings = (menu && menu._warnings) || [];

  return (
    <section className="ck-section">
      <h2 className="ck-section-title">Office menu</h2>

      <button className="fl-btn" onClick={runReseed} disabled={reseed.state === 'running'}>
        {reseed.state === 'running' ? 'Scraping…' : 'Reseed menu'}
      </button>
      {reseed.state === 'error' && <p className="fl-error">{reseed.error}</p>}
      {reseed.state === 'done' && reseed.data && (
        <p className="ck-hint">
          Week of {reseed.data.week_of} · {reseed.data.items_scraped} items scraped
        </p>
      )}
      {reseed.data?.warnings && reseed.data.warnings.length > 0 && (
        <div className="ck-warn-banner">
          {reseed.data.warnings.map((w, i) => <p key={i}>{w}</p>)}
        </div>
      )}

      {menuState === 'loading' && <p className="ck-note">Loading menu…</p>}
      {menuState === 'error' && <p className="fl-error">{menuError}</p>}
      {menuState === 'empty' && (
        <p className="ck-note">Menu not loaded yet — reseed above to fetch it.</p>
      )}

      {menuState === 'ready' && menu && (
        <>
          <p className="ck-hint">Week of {menu.week_of}</p>
          {menuWarnings.length > 0 && (
            <div className="ck-warn-banner">
              {menuWarnings.map((w, i) => <p key={i}>{w}</p>)}
            </div>
          )}
          <div className="ck-menu-days">
            {MENU_DAYS.filter((d) => menu[d]).map((day) => {
              const dayValue = menu[day];
              if (typeof dayValue === 'string') {
                return (
                  <div className="ck-menu-day" key={day}>
                    <h3 className="ck-menu-day-title">{day}</h3>
                    <p className="ck-note">{dayValue}</p>
                  </div>
                );
              }
              return (
                <div className="ck-menu-day" key={day}>
                  <h3 className="ck-menu-day-title">{day}</h3>
                  {MENU_MEALS.map((meal) => {
                    const items = dayValue?.[meal];
                    if (!items || items.length === 0) return null;
                    return (
                      <div className="ck-menu-meal" key={meal}>
                        <span className="ck-menu-meal-label">{meal}</span>
                        <ul className="ck-menu-items">
                          {items.map((item, idx) => {
                            const key = `${day}-${meal}-${idx}`;
                            const isOpen = openItem === key;
                            return (
                              <li className="ck-menu-item" key={key}>
                                <button
                                  type="button"
                                  className="ck-menu-item-row"
                                  onClick={() => setOpenItem(isOpen ? null : key)}
                                >
                                  <span className="ck-menu-item-name">{item.name}</span>
                                  {item.dietary_tags?.map((tag) => (
                                    <span className={`ck-tag ck-tag-${tag}`} key={tag}>{tag}</span>
                                  ))}
                                  <span className="ck-menu-item-kcal">
                                    {Math.round(item.kcal_per_100g)} kcal/100g
                                  </span>
                                </button>
                                {isOpen && (
                                  <div className="ck-menu-item-detail">
                                    <span>Protein {item.protein_per_100g}g</span>
                                    <span>Carbs {item.carbs_per_100g}g</span>
                                    <span>Sugar {item.sugar_per_100g}g</span>
                                    <span>Fat {item.fat_per_100g}g</span>
                                    <span>Sat. fat {item.saturated_fat_per_100g}g</span>
                                    <span>Salt {item.salt_per_100g}g</span>
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}

const DASHBOARDS = [
  { href: `https://vercel.com/${VERCEL_PROJECT}`, label: 'Vercel · project' },
  { href: `https://vercel.com/${VERCEL_PROJECT}/deployments`, label: 'Vercel · deployments' },
  { href: `https://vercel.com/${VERCEL_PROJECT}/logs`, label: 'Vercel · runtime logs' },
  { href: `https://vercel.com/${VERCEL_PROJECT}/settings/cron-jobs`, label: 'Vercel · cron jobs' },
  { href: 'https://console.cloudinary.com/', label: 'Cloudinary · media library' },
  { href: `https://supabase.com/dashboard/project/${SUPABASE_PROJECT}/editor`, label: 'Supabase · food_log table' },
];

export default function Cockpit() {
  const [pin, setPin] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [pinError, setPinError] = useState('');

  const [backfill, setBackfill] = useState({ state: 'idle', data: null, error: '' });
  const [copied, setCopied] = useState(false);

  function submitPin(e) {
    e.preventDefault();
    if (pin === CORRECT_PIN) setUnlocked(true);
    else setPinError('Wrong PIN');
  }

  async function runBackfill() {
    setBackfill({ state: 'running', data: null, error: '' });
    try {
      const r = await fetch(`/api/geocode-backfill?pin=${encodeURIComponent(pin)}`);
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      setBackfill({ state: 'done', data, error: '' });
    } catch (err) {
      setBackfill({ state: 'error', data: null, error: err.message });
    }
  }

  function copyPurge() {
    navigator.clipboard.writeText(PURGE_CMD).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  if (!unlocked) {
    return (
      <div className="fl-page">
        <h1 className="fl-title">Cockpit</h1>
        <form className="fl-form" onSubmit={submitPin}>
          <input
            className="fl-pin-input"
            type="password"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="Enter PIN"
            autoFocus
          />
          {pinError && <p className="fl-error">{pinError}</p>}
          <button className="fl-btn" type="submit">Continue</button>
        </form>
        <a className="fl-back" href="/">← Home</a>
      </div>
    );
  }

  const bf = backfill.data;

  return (
    <div className="fl-page fl-page-wide ck">
      <h1 className="fl-title">Cockpit</h1>

      <section className="ck-section">
        <h2 className="ck-section-title">Uploads</h2>
        <div className="ck-links">
          {UPLOADS.map((u) => (
            <a className="ck-link" href={u.href} key={u.href}>
              <span className="ck-link-main">{u.label}</span>
              <span className="ck-link-desc">{u.desc}</span>
              <span className="ck-link-arrow" aria-hidden="true">→</span>
            </a>
          ))}
        </div>
      </section>

      <section className="ck-section">
        <h2 className="ck-section-title">Map data</h2>

        <button
          className="fl-btn"
          onClick={runBackfill}
          disabled={backfill.state === 'running'}
        >
          {backfill.state === 'running' ? 'Running… (~25s)' : 'Run geocode backfill'}
        </button>
        {backfill.state === 'error' && <p className="fl-error">{backfill.error}</p>}
        {bf && (
          <pre className="ck-result">
{`eligible:  ${bf.eligible}
processed: ${bf.processed}
updated:   ${bf.updatedCount}
remaining: ${bf.remaining}`}
            {bf.errors && bf.errors.length ? `\nerrors:    ${bf.errors.length}` : ''}
            {bf.updated && bf.updated.length
              ? '\n\n' + bf.updated.map((u) => `  ${u.from}  →  ${u.to}`).join('\n')
              : ''}
          </pre>
        )}

        <div className="ck-cmd">
          <span className="ck-cmd-label">Purge CDN cache — run locally:</span>
          <code className="ck-cmd-code">{PURGE_CMD}</code>
          <button className="ck-cmd-copy" onClick={copyPurge}>{copied ? 'Copied' : 'Copy'}</button>
        </div>
      </section>

      <MenuWidget />

      <section className="ck-section">
        <h2 className="ck-section-title">Dashboards</h2>
        <div className="ck-links">
          {DASHBOARDS.map((d) => (
            <a
              className="ck-link ck-link-ext"
              href={d.href}
              key={d.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="ck-link-main">{d.label}</span>
              <span className="ck-link-arrow" aria-hidden="true">↗</span>
            </a>
          ))}
        </div>
      </section>

      <section className="ck-section">
        <h2 className="ck-section-title">Notes</h2>
        <p className="ck-note">
          New blog post is local-only: add <code>public/posts/&lt;slug&gt;.md</code>,
          run <code>node scripts/generate-posts.js</code>, commit &amp; push.
        </p>
        <p className="ck-note">
          Geocode backfill also runs nightly (~04:00 UTC). New map photos show
          their offline name until it catches up.
        </p>
      </section>

      <a className="fl-back" href="/">← Home</a>
    </div>
  );
}
